import React, { useState, useEffect, useRef } from 'react';
import { auth, googleProvider, db } from './firebaseConfig';
import { signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { User, Calendar, ArrowRight } from 'lucide-react';

// --- DEFINIÇÃO DAS ESCALAS ---
const escalas = {
  frequencia: { 
    1: { texto: "Nunca", cor: "text-red-600 font-bold", detalhe: "Não faz parte da minha rotina." }, 
    2: { texto: "Raramente", cor: "text-orange-500", detalhe: "Acontece muito de vez em quando." }, 
    3: { texto: "Às vezes", cor: "text-yellow-600", detalhe: "Faço quando dá, sem regularidade." }, 
    4: { texto: "Frequentemente", cor: "text-blue-500", detalhe: "Tenho disciplina, mas ainda exige esforço." }, 
    5: { texto: "Hábito / Sempre", cor: "text-green-600 font-bold", detalhe: "É automático. Faço sem pensar (Identidade)." } 
  },
  nivel: { 
    1: { texto: "Muito Ruim", cor: "text-red-600 font-bold", detalhe: "Prejudica minha qualidade de vida." }, 
    2: { texto: "Baixo", cor: "text-orange-500", detalhe: "Precisa de bastante atenção." }, 
    3: { texto: "Regular", cor: "text-yellow-600", detalhe: "Está na média, mas pode melhorar." }, 
    4: { texto: "Bom", cor: "text-blue-500", detalhe: "Estou satisfeito, funciona bem." }, 
    5: { texto: "Excelente", cor: "text-green-600 font-bold", detalhe: "Estado pleno. É um ponto forte meu." } 
  },
  detrator: {
    0: { texto: "Não / Nunca", cor: "text-green-600 font-bold", detalhe: "Não se aplica a mim. Estou livre disso." },
    1: { texto: "Muito Leve", cor: "text-blue-500", detalhe: "Raríssimo. Impacto quase irrelevante." },
    2: { texto: "Leve / Ocasional", cor: "text-yellow-600", detalhe: "Acontece às vezes, mas tenho total controle." },
    3: { texto: "Moderado", cor: "text-orange-500", detalhe: "É recorrente e já começa a me preocupar." },
    4: { texto: "Grave", cor: "text-red-500", detalhe: "É frequente e causa prejuízos visíveis." },
    5: { texto: "Crítico", cor: "text-red-700 font-bold", detalhe: "Fora de controle. Risco alto ou dependência." }
  }
};

// ORDEM DAS PERGUNTAS
const perguntas = {
  fisica: [
    { texto: "Movimento Diário", tipo: "frequencia", pergunta: "Você se mantém em movimento ao longo do dia, evitando ficar muito tempo sentado?", ajuda: "Não se refere apenas à academia, mas ao 'movimento natural': subir escadas, caminhar até lugares próximos, levantar-se a cada hora e acumular passos." },
    { texto: "Força Funcional", tipo: "nivel", pergunta: "Você sente que seus músculos são fortes o suficiente para lhe dar total autonomia?", ajuda: "Refere-se à massa muscular necessária para a vida real: levantar de uma cadeira sem apoio, carregar sacolas ou pegar objetos no chão." },
    { texto: "Capacidade Aeróbica", tipo: "frequencia", pergunta: "Você consegue realizar esforços moderados sem ficar excessivamente ofegante?", ajuda: "É a saúde do coração e pulmões. Subir dois lances de escada ou caminhar rápido mantendo uma conversa." },
    { texto: "Flexibilidade & Mobilidade", tipo: "nivel", pergunta: "Suas articulações são livres e você se movimenta sem rigidez ou dor?", ajuda: "Capacidade de realizar movimentos amplos, como amarrar os sapatos ou alcançar algo no alto, sentindo o corpo solto." },
    { texto: "Sono Restaurador", tipo: "nivel", pergunta: "Você acorda sentindo-se realmente descansado e com energia para o dia?", ajuda: "Dormir profundamente, sem depender de remédios para apagar ou excesso de estimulantes para acordar." },
    { texto: "Nutrição Essencial", tipo: "nivel", pergunta: "Sua alimentação diária prioriza alimentos naturais em vez de industrializados?", ajuda: "'Descascar mais, desembalar menos'. Ingestão de proteínas suficientes e evitar excesso de açúcar e ultraprocessados." },
    { texto: "Substâncias & Toxidade", tipo: "detrator", pergunta: "Você faz uso de álcool, tabaco, anabolizantes ou automedicação frequente?", ajuda: "Fatores que agridem a biologia: hormônios sem indicação, cigarro/vape, álcool regular, remédios sem prescrição." }
  ],
  mental: [
    { texto: "Atenção & Presença", tipo: "frequencia", pergunta: "Você consegue manter o foco no momento presente, sem se distrair facilmente?", ajuda: "Reduzir o ruído mental e multitarefa. Estar onde seu corpo está." },
    { texto: "Estímulo Cognitivo", tipo: "frequencia", pergunta: "Com que frequência você aprende algo novo ou desafia seu cérebro?", ajuda: "Neuroplasticidade: aprender idiomas, instrumentos, jogos de estratégia ou novas tecnologias." },
    { texto: "Gestão do Estresse", tipo: "nivel", pergunta: "Você consegue lidar com a pressão do dia a dia sem adoecer ou se exaurir?", ajuda: "Ter 'válvulas de escape' saudáveis e transformar tensão em ação, evitando ansiedade crônica." },
    { texto: "Mentalidade de Crescimento", tipo: "nivel", pergunta: "Você encara erros e mudanças como oportunidades de aprendizado?", ajuda: "Growth Mindset. Disposição para mudar de opinião e não acreditar que 'não se aprende truque novo'." },
    { texto: "Criatividade & Imaginação", tipo: "frequencia", pergunta: "Você usa sua imaginação para encontrar soluções novas para problemas antigos?", ajuda: "Agilidade mental para resolver imprevistos, improvisar e enxergar possibilidades." },
    { texto: "Planejamento & Organização", tipo: "nivel", pergunta: "Você sente que está no comando do seu tempo e das suas prioridades?", ajuda: "Rotina intencional. A diferença entre agir com intenção e reagir às urgências dos outros." },
    { texto: "Estimulantes & Sobrecarga", tipo: "detrator", pergunta: "Você depende de energéticos/foco artificial ou sofre com excesso de telas?", ajuda: "Uso de 'pílulas da inteligência', excesso de cafeína ou vício em redes sociais (doomscrolling)." }
  ],
  emocional: [
    { texto: "Autoconhecimento", tipo: "nivel", pergunta: "Você consegue identificar e nomear exatamente o que está sentindo?", ajuda: "Saber diferenciar 'tristeza' de 'frustração' ou 'cansaço' e entender os gatilhos." },
    { texto: "Autorregulação", tipo: "frequencia", pergunta: "Diante de um problema, você consegue responder com calma em vez de explodir?", ajuda: "Fazer uma pausa entre o estímulo e a reação. Não agir por impulso." },
    { texto: "Resiliência & Adaptação", tipo: "nivel", pergunta: "Quando algo dá errado, quão rápido você consegue se levantar e ajustar a rota?", ajuda: "Não paralisar diante das crises; aceitar a realidade e seguir em frente." },
    { texto: "Autoconfiança", tipo: "nivel", pergunta: "Você confia na sua própria capacidade de lidar com os desafios da vida?", ajuda: "Autoeficácia. Acreditar que, venha o que vier, você dará conta." },
    { texto: "Comunicação Controlada", tipo: "frequencia", pergunta: "Você consegue se expressar e ouvir os outros sem perder o controle emocional?", ajuda: "Falar sem agressividade e escutar críticas sem levar para o lado pessoal." },
    { texto: "Legado Positivo", tipo: "nivel", pergunta: "Suas emoções e atitudes costumam inspirar ou drenar as pessoas ao redor?", ajuda: "O 'rastro' emocional que você deixa. Encorajamento vs. reclamação." },
    { texto: "Toxicidade Emocional", tipo: "detrator", pergunta: "Você guarda mágoas antigas, rancor ou vive padrões de alta reatividade?", ajuda: "Manter 'lixo emocional' guardado, não perdoar ou viver explodindo." }
  ],
  espiritual: [
    { texto: "Propósito & Significado", tipo: "nivel", pergunta: "Você tem uma razão clara que te faz querer levantar da cama todas as manhãs?", ajuda: "Ikigai. Um projeto, causa social, família ou aprendizado. Ter um 'porquê'." },
    { texto: "Valores & Princípios", tipo: "nivel", pergunta: "Suas ações diárias estão alinhadas com o que você diz ser importante?", ajuda: "Coerência. Viver de acordo com sua consciência e valores profundos." },
    { texto: "Práticas de Conexão", tipo: "frequencia", pergunta: "Você reserva momentos de pausa para se reconectar consigo ou com o sagrado?", ajuda: "Meditação, oração, respiração ou contemplar a natureza." },
    { texto: "Transcendência & Silêncio", tipo: "frequencia", pergunta: "Você consegue ficar em silêncio e ouvir sua intuição ou voz interior?", ajuda: "Tolerar o silêncio. Onde surgem insights e percepção de algo maior." },
    { texto: "Gratidão & Contentamento", tipo: "frequencia", pergunta: "Você costuma focar no que já tem ou vive pensando no que falta?", ajuda: "Treinar o olhar para o 'copo meio cheio'. Valorizar pequenas vitórias." },
    { texto: "Serviço & Contribuição", tipo: "frequencia", pergunta: "Você dedica parte do seu tempo ou talento para ajudar o próximo sem esperar troca?", ajuda: "Altruísmo. Sentir que sua vida é útil para outras pessoas." },
    { texto: "Vazio Existencial", tipo: "detrator", pergunta: "Você sente um vazio constante ou foca apenas no materialismo?", ajuda: "Sensação de que nada importa (niilismo) ou viver apenas para o ego." }
  ],
  financeira: [
    { texto: "Consciência Financeira", tipo: "nivel", pergunta: "Você sabe exatamente quanto ganha, quanto gasta e para onde vai seu dinheiro?", ajuda: "Clareza dos números. Não ignorar extratos. Saber a verdade financeira." },
    { texto: "Organização & Planejamento", tipo: "nivel", pergunta: "Seu dinheiro tem um destino definido antes mesmo de cair na conta?", ajuda: "Ter orçamento. Definir prioridades e direção estratégica para recursos." },
    { texto: "Reserva de Emergência", tipo: "nivel", pergunta: "Se um imprevisto acontecer hoje, você tem recursos para resolver sem se endividar?", ajuda: "Colchão financeiro para dormir em paz caso surja um gasto médico ou quebra." },
    { texto: "Investimentos & Futuro", tipo: "frequencia", pergunta: "Você poupa sistematicamente pensando na qualidade de vida do seu 'eu' futuro?", ajuda: "Plantar hoje para colher amanhã. Juros compostos visando longevidade." },
    { texto: "Riscos & Proteções", tipo: "nivel", pergunta: "Você e seu patrimônio estão protegidos contra grandes imprevistos da vida?", ajuda: "Seguros (vida, saúde, auto) e evitar riscos desnecessários." },
    { texto: "Equilíbrio & Autonomia", tipo: "nivel", pergunta: "Seu padrão de vida cabe confortavelmente dentro da sua renda atual?", ajuda: "Viver um degrau abaixo do que ganha. Ausência de dívidas tóxicas." },
    { texto: "Risco & Descontrole", tipo: "detrator", pergunta: "Você tem dívidas caras, aposta dinheiro ou compra por impulso?", ajuda: "Juros de cartão, jogos de azar ou compulsão que destrói patrimônio." }
  ],
  social: [
    { texto: "Família & Relações", tipo: "nivel", pergunta: "Você investe energia intencional nas relações primárias, praticando o afeto e o perdão?", ajuda: "Tempo de qualidade com quem importa. Prática do afeto e perdão." },
    { texto: "Amizades Nutritivas", tipo: "nivel", pergunta: "Você se cerca de pessoas que compartilham seus valores e torcem pelo seu crescimento?", ajuda: "Curadoria social. Pessoas que apoiam seu crescimento e estão lá nos tempos difíceis." },
    { texto: "Comunidade & Rede", tipo: "nivel", pergunta: "Você participa ativamente de grupos ou causas que geram sentimento de pertencimento?", ajuda: "Antídoto para solidão. Participar de grupos, clubes ou associações." },
    { texto: "Convívio Presente", tipo: "frequencia", pergunta: "Você consegue desligar distrações para estar de corpo e alma com o outro (olho no olho)?", ajuda: "Presença real. Desligar telas, escuta ativa e valorizar quem está à frente." },
    { texto: "Experiências & Memórias", tipo: "frequencia", pergunta: "Você investe na criação de momentos, encontros e histórias compartilhadas?", ajuda: "Investir em vivências. Histórias em conjunto enriquecem a biografia." },
    { texto: "Ambiente & Entorno", tipo: "nivel", pergunta: "O ambiente onde você vive e trabalha favorece sua segurança, saúde e contato com a natureza?", ajuda: "O espaço molda a saúde. Segurança, ergonomia, natureza e menos toxinas." },
    { texto: "Isolamento ou Ambiente Tóxico", tipo: "detrator", pergunta: "Você sofre com isolamento social, relações conflituosas ou vive em ambiente insalubre?", ajuda: "Isolamento, conflitos constantes ou ambientes inseguros/insalubres." }
  ]
};

// COMPONENTE QUESTIONÁRIO
const Questionario = ({ aoFinalizar, onAbrirTermos, valoresIniciais }) => {
  // --- ESTADOS ---
  const [etapa, setEtapa] = useState(valoresIniciais?.demograficos ? 'perguntas' : 'demograficos');
  const [demograficos, setDemograficos] = useState(valoresIniciais?.demograficos || { idade: '', genero: '' });
  const [respostas, setRespostas] = useState(valoresIniciais?.respostas || {});
  
  const [ajudaAberta, setAjudaAberta] = useState(null);
  const [usuario, setUsuario] = useState(null);
  const [statusSalvo, setStatusSalvo] = useState("Aguardando login...");
  const [carregandoLogin, setCarregandoLogin] = useState(true);
  const [mostrarTermosLogin, setMostrarTermosLogin] = useState(false);
  
  const processandoLoginManual = useRef(false);

  // EFEITO: Monitora Login e recupera dados
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCarregandoLogin(false);
      if (processandoLoginManual.current) return;

      if (user) {
        setUsuario(user);
        setStatusSalvo("Sincronizando...");
        try {
          const docRef = doc(db, "usuarios", user.uid);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const dados = docSnap.data();
            // Carrega respostas
            if (Object.keys(respostas).length === 0 && dados.respostas) {
              setRespostas(dados.respostas);
            }
            // Carrega demográficos, mas NÃO PULA MAIS A TELA
            if (dados.demograficos) {
              setDemograficos(dados.demograficos);
              // setEtapa('perguntas'); // REMOVIDO: Agora sempre mostra a tela para revisão
            }
            setStatusSalvo("Dados carregados!");
          }
        } catch (e) {
          console.error("Erro ao buscar dados:", e);
        }
      } else {
        setUsuario(null);
        setStatusSalvo("Visitante");
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- FUNÇÕES DE DEMOGRAFIA ---
  const handleDemograficoChange = (campo, valor) => {
    setDemograficos(prev => ({ ...prev, [campo]: valor }));
  };

  const avancarParaPerguntas = async () => {
    if (demograficos.idade && demograficos.genero) {
      // Se o usuário estiver logado, já atualiza o cadastro com a nova idade/genero
      // Isso garante que mudanças (ex: fez aniversário) sejam salvas
      if (usuario) {
        try {
            await setDoc(doc(db, "usuarios", usuario.uid), {
                demograficos: demograficos
            }, { merge: true });
        } catch (e) {
            console.error("Erro ao atualizar demografia:", e);
        }
      }
      
      setEtapa('perguntas');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      alert("Por favor, preencha sua idade e gênero para prosseguir.");
    }
  };

  // --- FUNÇÕES DO QUESTIONÁRIO ---
  const handleChange = (dimensao, index, valor) => {
    setRespostas(prev => ({ ...prev, [`${dimensao}-${index}`]: parseInt(valor) }));
  };

  const toggleAjuda = (id) => { if (ajudaAberta === id) setAjudaAberta(null); else setAjudaAberta(id); };

  const handleLoginESalvar = async () => {
    if (!usuario) {
        setMostrarTermosLogin(true);
        return;
    }
    try {
      setStatusSalvo("Salvando...");
      await setDoc(doc(db, "usuarios", usuario.uid), {
        respostas: respostas,
        demograficos: demograficos,
        ultimaAtualizacao: new Date(),
        termosAceitos: true 
      }, { merge: true });
      setStatusSalvo("✅ Salvo!");
      setTimeout(() => setStatusSalvo(""), 3000);
    } catch (error) {
      console.error(error);
      setStatusSalvo("Erro ao salvar");
    }
  };

  const handleAceiteFinal = async () => {
    setMostrarTermosLogin(false);
    setStatusSalvo("Conectando...");
    processandoLoginManual.current = true;
    
    try {
        const result = await signInWithPopup(auth, googleProvider);
        const userAtual = result.user;
        setUsuario(userAtual); 
        
        setStatusSalvo("Mesclando dados...");
        const docRef = doc(db, "usuarios", userAtual.uid);
        const docSnap = await getDoc(docRef);
        
        let respostasFinais = respostas; 
        let demograficosFinais = demograficos;

        if (docSnap.exists()) {
            const dadosBanco = docSnap.data();
            if (Object.keys(respostas).length > 0) {
                respostasFinais = { ...(dadosBanco.respostas || {}), ...respostas };
            } else {
                respostasFinais = dadosBanco.respostas || {};
            }
            // Se já preencheu na tela agora, usa o atual. Se não, tenta pegar do banco.
            if (demograficos.idade && demograficos.genero) {
                demograficosFinais = demograficos;
            } else {
                demograficosFinais = dadosBanco.demograficos || {};
            }
        }

        await setDoc(docRef, {
            email: userAtual.email,
            nome: userAtual.displayName,
            termosAceitos: true,
            dataAceite: new Date(),
            ultimaAtualizacao: new Date(),
            respostas: respostasFinais,
            demograficos: demograficosFinais
        }, { merge: true });

        setRespostas(respostasFinais);
        setDemograficos(demograficosFinais);
        
        // REMOVIDO O PULO AUTOMÁTICO AQUI TAMBÉM
        // O usuário verá os dados preenchidos e clicará em continuar
        setStatusSalvo("✅ Conectado!");
        
        setTimeout(() => {
            processandoLoginManual.current = false;
            setStatusSalvo("");
        }, 3000);

    } catch (error) {
        console.error("Erro:", error);
        processandoLoginManual.current = false; 
        if (error.code === 'auth/popup-closed-by-user') setStatusSalvo("Cancelado");
        else setStatusSalvo("Erro no Login");
    }
  };

  const handleLogout = () => {
    signOut(auth).then(() => {
      setUsuario(null);
      setRespostas({});
      setDemograficos({ idade: '', genero: '' });
      setEtapa('demograficos');
      setStatusSalvo("Desconectado");
    });
  };

  const calcularResultado = () => {
    try {
      const dimensoes = Object.keys(perguntas);
      let somaTotalGeral = 0;
      const maximoPossivel = 36 * 4; 

      const dadosGrafico = dimensoes.map(dim => {
        let somaPositiva = 0;
        let somaNegativa = 0;
        
        let tituloVisual = dim.charAt(0).toUpperCase() + dim.slice(1);
        if (dim === 'social') tituloVisual = "Social e Ambiental";

        perguntas[dim].forEach((item, idx) => {
          const padrao = item.tipo === 'detrator' ? 0 : 1;
          let valor = respostas[`${dim}-${idx}`];
          if (valor === undefined || valor === null) valor = padrao;
          
          if (item.tipo === 'detrator') {
            somaNegativa += valor;
          } else {
            somaPositiva += (valor - 1);
          }
        });

        let pontuacaoLiquida = somaPositiva - somaNegativa;
        if (pontuacaoLiquida < 0) pontuacaoLiquida = 0;

        somaTotalGeral += pontuacaoLiquida;

        const notaPercentual = Math.round((pontuacaoLiquida / 24) * 100);
        const perdaPercentual = Math.round((somaNegativa / 24) * 100);

        return { 
          dimensao: tituloVisual, 
          notaAtual: isNaN(notaPercentual) ? 0 : (notaPercentual > 100 ? 100 : (notaPercentual < 0 ? 0 : notaPercentual)), 
          perdaDetrator: isNaN(perdaPercentual) ? 0 : perdaPercentual,
          fullMark: 100 
        };
      });
      
      let indiceFinal = Math.round((somaTotalGeral / maximoPossivel) * 100);
      if (isNaN(indiceFinal)) indiceFinal = 0;
      if (indiceFinal < 0) indiceFinal = 0;

      const dataGeracao = new Date();

      if(usuario) {
          handleLoginESalvar().catch(err => console.error("Erro background:", err));
      }
      
      aoFinalizar({ 
          grafico: dadosGrafico, 
          indice: indiceFinal > 100 ? 100 : indiceFinal,
          respostas: respostas,
          demograficos: demograficos,
          data: dataGeracao
      });

    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const getValorAtual = (dimensao, index, tipo) => {
    const padrao = tipo === 'detrator' ? 0 : 1;
    return respostas[`${dimensao}-${index}`] !== undefined ? respostas[`${dimensao}-${index}`] : padrao;
  };

  // --- RENDERIZAÇÃO ---

  return (
    <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl mx-auto border border-gray-200 relative pb-6">
      
      {/* LOADING OVERLAY (USANDO A VARIÁVEL CARREGANDOLOGIN) */}
      {carregandoLogin && (
        <div className="absolute inset-0 bg-white/90 z-[60] flex items-center justify-center rounded-xl backdrop-blur-sm">
            <span className="text-blue-600 font-bold animate-pulse text-lg flex items-center gap-2">
                <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Carregando...
            </span>
        </div>
      )}

      {/* MENU FLUTUANTE */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm p-4 rounded-t-xl mb-6 flex flex-col md:flex-row justify-between items-center gap-4 transition-all">
        <div>
            <h2 className="text-xl font-bold text-gray-800">Diagnóstico 6x6</h2>
            <div className="flex gap-2 text-xs mt-1">
                 <span className={`px-2 py-0.5 rounded font-bold ${usuario ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {statusSalvo}
                 </span>
            </div>
        </div>
        
        <div className="flex items-center gap-3">
            {!usuario ? (
                <div className="flex gap-2">
                    <button onClick={handleLoginESalvar} className="flex items-center gap-1 text-gray-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 border border-gray-200">Recuperar</button>
                    <button onClick={handleLoginESalvar} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-blue-700 shadow-md hover:scale-105">Salvar</button>
                </div>
            ) : (
                <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200">
                    {usuario.photoURL ? <img src={usuario.photoURL} alt="User" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">{usuario.displayName?.charAt(0)}</div>}
                    <span className="text-xs text-gray-800 font-bold max-w-[80px] truncate hidden md:block">{usuario.displayName?.split(' ')[0]}</span>
                    <button onClick={handleLoginESalvar} className="ml-2 p-2 bg-white rounded-full text-blue-600 hover:text-blue-800 shadow border border-gray-100" title="Salvar agora"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-4 4m0 0l-4-4m4 4V4" /></svg></button>
                    <button onClick={handleLogout} className="ml-1 p-2 bg-red-50 rounded-full text-red-500 hover:bg-red-100 border border-red-100" title="Sair"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg></button>
                </div>
            )}
        </div>
      </div>

      {/* --- ETAPA 1: DADOS DEMOGRÁFICOS --- */}
      {etapa === 'demograficos' && (
        <div className="px-6 py-8 animate-fade-in">
          <div className="max-w-xl mx-auto text-center space-y-8">
            <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100">
              <h3 className="text-2xl font-bold text-blue-900 mb-2">Sobre você</h3>
              <p className="text-blue-700 text-sm mb-6">
                Para tornar seu diagnóstico mais preciso e permitir futuras comparações com pessoas do seu perfil, precisamos de dois dados essenciais.
              </p>

              <div className="space-y-6 text-left">
                {/* IDADE */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <Calendar size={18} className="text-blue-500" /> Sua Idade Agora
                  </label>
                  <input 
                    type="number" 
                    placeholder="Ex: 45"
                    min="18"
                    max="120"
                    value={demograficos.idade}
                    onChange={(e) => handleDemograficoChange('idade', e.target.value)}
                    className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-lg"
                  />
                </div>

                {/* GÊNERO */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                    <User size={18} className="text-blue-500" /> Sexo Biológico
                  </label>
                  <p className="text-xs text-gray-500 mb-3">Utilizado para referência fisiológica e hormonal.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <button 
                      onClick={() => handleDemograficoChange('genero', 'masculino')}
                      className={`p-4 rounded-xl border-2 font-bold transition-all ${demograficos.genero === 'masculino' ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-blue-300 text-gray-500'}`}
                    >
                      Masculino
                    </button>
                    <button 
                      onClick={() => handleDemograficoChange('genero', 'feminino')}
                      className={`p-4 rounded-xl border-2 font-bold transition-all ${demograficos.genero === 'feminino' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-gray-200 hover:border-pink-300 text-gray-500'}`}
                    >
                      Feminino
                    </button>
                  </div>
                </div>
              </div>

              <button 
                onClick={avancarParaPerguntas}
                className={`mt-8 w-full py-4 rounded-xl font-bold text-lg shadow-lg flex items-center justify-center gap-2 transition-all ${demograficos.idade && demograficos.genero ? 'bg-blue-600 text-white hover:bg-blue-700 hover:scale-105' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              >
                Continuar para Diagnóstico <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* --- ETAPA 2: QUESTIONÁRIO (SÓ APARECE SE ETAPA FOR 'perguntas') --- */}
      {etapa === 'perguntas' && (
        <div className="animate-fade-in">
          {/* INSTRUÇÕES */}
          <div className="mx-6 mb-8 mt-2 p-5 bg-blue-50 border-l-4 border-blue-500 rounded-r-xl shadow-sm">
            <h3 className="text-blue-900 font-bold text-lg mb-2 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>
                Antes de começar
            </h3>
            <ul className="text-sm text-blue-800 space-y-2 leading-relaxed">
                <li className="flex items-start gap-2"><span>🧘</span><span><strong>Responda com calma.</strong> Este é um momento de autoconhecimento.</span></li>
                <li className="flex items-start gap-2"><span>ℹ️</span><span>Use o botão <span className="inline-block bg-white text-blue-500 rounded-full px-1 py-0 border border-blue-200"><svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 inline" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 001-1 1 1 0 00-1 1zM10 14a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" /></svg></span> para entender bem o conceito por trás de cada questão.</span></li>
                <li className="flex items-start gap-2"><span>⚠️</span><span><strong>Atenção aos Detratores:</strong> Ao final de cada bloco, há uma pergunta sobre hábitos nocivos. Seja honesto!</span></li>
            </ul>
          </div>

          <div className="space-y-10 px-6">
            {Object.entries(perguntas).map(([dimensao, listaPerguntas]) => (
              <div key={dimensao} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-2 h-8 bg-blue-600 rounded-full"></span>
                  <h3 className="text-2xl font-bold text-gray-800 capitalize">
                      {dimensao === 'social' ? 'Dimensão Social e Ambiental' : `Dimensão ${dimensao}`}
                  </h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                  {listaPerguntas.map((item, idx) => {
                    const valor = getValorAtual(dimensao, idx, item.tipo);
                    const idUnico = `${dimensao}-${idx}`;
                    const estaAberto = ajudaAberta === idUnico;
                    const tipoEscala = item.tipo || 'frequencia';
                    const legendaAtual = tipoEscala === 'detrator' ? escalas.detrator : escalas[tipoEscala];
                    const ehDetrator = item.tipo === 'detrator';

                    return (
                      <div key={idx} className={`flex flex-col relative ${ehDetrator ? 'md:col-span-2 mt-4 pt-6 border-t-2 border-dashed border-red-200' : ''}`}>
                        {ehDetrator && (
                            <div className="mb-2 flex items-center gap-2 text-red-600 font-bold text-sm uppercase tracking-wide">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                                Fator de Risco (Detrator)
                            </div>
                        )}
                        <div className="flex justify-between items-start mb-2 gap-2">
                          <label className={`text-sm font-bold leading-tight ${ehDetrator ? 'text-red-800' : 'text-gray-800'}`}>{item.texto}</label>
                          <button onClick={() => toggleAjuda(idUnico)} className="text-blue-500 hover:text-blue-700 transition-colors" title="Entenda o significado">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 001-1 1 1 0 00-1 1zM10 14a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" /></svg>
                          </button>
                        </div>
                        <p className="text-sm text-gray-600 mb-3 min-h-[2.5rem]">{item.pergunta}</p>
                        {estaAberto && (
                          <div className="absolute top-12 left-0 right-0 z-10 bg-blue-50 p-4 rounded-lg border border-blue-200 shadow-xl text-sm text-blue-900 mb-2 animate-fade-in">
                            <strong>Significado:</strong><br/>{item.ajuda}
                            <button onClick={() => toggleAjuda(null)} className="block mt-3 text-blue-600 font-bold underline text-xs w-full text-right">Fechar ajuda</button>
                          </div>
                        )}
                        <div className="flex justify-between items-end mb-2">
                          <span className={`text-2xl font-bold ${ehDetrator ? 'text-red-700' : 'text-gray-800'}`}>{valor}</span>
                          <span className={`text-xs font-medium ${legendaAtual[valor].cor}`}>{legendaAtual[valor].texto}</span>
                        </div>
                        <input 
                          type="range" 
                          min={ehDetrator ? "0" : "1"} 
                          max="5" 
                          step="1" 
                          value={valor} 
                          onChange={(e) => handleChange(dimensao, idx, e.target.value)} 
                          className={`w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer ${ehDetrator ? 'accent-red-600' : 'accent-blue-600'} hover:accent-opacity-80 transition-all`} 
                        />
                        <p className={`text-[10px] mt-1 h-3 text-right ${legendaAtual[valor].cor}`}>
                            {legendaAtual[valor].detalhe}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end items-center">
                    <button
                        onClick={handleLoginESalvar}
                        className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {usuario ? `Salvar Progresso` : "Salvar Progresso"}
                    </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center pb-8">
            <button onClick={calcularResultado} className="bg-blue-900 hover:bg-blue-800 text-white font-bold py-4 px-12 rounded-full text-lg shadow-xl hover:scale-105 transition-transform animate-pulse">
              Gerar Relatório Completo 📊
            </button>
          </div>
        </div>
      )}

      {/* --- MODAL DE TERMOS E ACEITE (Mantido) --- */}
      {mostrarTermosLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in-up">
            <div className="p-6 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-800">Antes de entrar...</h3>
                <p className="text-sm text-gray-500">Precisamos do seu acordo para prosseguir.</p>
            </div>
            
            <div className="p-6 max-h-[60vh] overflow-y-auto text-sm text-gray-600 space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <strong className="block text-blue-900 mb-1">🛡️ Privacidade e LGPD</strong>
                    <p>Seus dados são usados <strong>exclusivamente</strong> para gerar seu diagnóstico. Não compartilhamos nada com terceiros.</p>
                </div>

                <div className="bg-red-50 p-4 rounded-lg border border-red-100 text-red-800">
                    <strong className="block text-red-900 mb-1">⚠️ Isenção de Responsabilidade</strong>
                    <p>Este app é educativo. <strong>Os resultados apresentados NÃO constituem diagnóstico médico, psicológico ou nutricional.</strong> Nunca altere sua saúde baseando-se apenas neste teste.</p>
                </div>

                <p className="text-xs text-gray-400 mt-4 leading-relaxed">
                    Ao clicar em "Li e Concordo", você aceita nossos Termos de Uso e Política de Privacidade e autoriza o login via Google.
                    <button 
                        onClick={() => onAbrirTermos()} 
                        className="text-blue-600 hover:underline bg-transparent border-none cursor-pointer ml-1 font-bold"
                    >
                        Ler termos completos.
                    </button>
                </p>
            </div>

            <div className="p-6 border-t border-gray-100 flex justify-between items-center bg-gray-50">
                <button 
                    onClick={() => setMostrarTermosLogin(false)}
                    className="text-gray-500 hover:text-gray-700 text-sm font-medium"
                >
                    Cancelar
                </button>
                <button 
                    onClick={handleAceiteFinal}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-lg flex items-center gap-2"
                >
                    <span>Li e Concordo</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Questionario;